import { zodResolver } from '@hookform/resolvers/zod'
import { Box } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useAppSelector } from '../../app/hooks'
import {
  useCreateWorkforceMutation,
  useGetUserTypesQuery,
  useUpdateUserMutation,
} from '../../app/redux-fetch/apiQuery'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import { useSideModalContext } from '../Assets/modals/SideModalContext'
import Documents from '../Documents/Documents'
import { useNotificationStack } from '../Shipments/hooks/useNotificationStack'
import { fetchLoggedInUserSelector } from '../selectors'
import { ILoggedInUser } from '../types'
import DriverInfo from './DriverInfo'
import UserInfo from './UserInfo'
import { WorkforceFormData, workforceSchema } from './schemas/workforceSchema'
import { IWorkforce } from './types'

type WorkforceFormProps = {
  workforce: IWorkforce
  isDisabled?: boolean
}

const WorkforceForm = ({ workforce, isDisabled }: WorkforceFormProps) => {
  const { onClose } = useSideModalContext()

  const isNew = !workforce.id
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)

  const tenantId = loggedInUser.tenantId ?? -1
  const customerId = loggedInUser.customerId

  const [createDriver] = useCreateWorkforceMutation()
  const [updateDriver] = useUpdateUserMutation()

  const { enqueue, enqueueFailureMsg } = useNotificationStack()

  const { data: userTypes } = useGetUserTypesQuery({ tenantId, customerId })

  const methods = useForm<WorkforceFormData>({
    resolver: zodResolver(workforceSchema),
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: {
      ...workforce,
      tenantId,
    },
  })

  const { handleSubmit, watch } = methods

  const userType = watch('userType')

  const onSubmit = async (values: WorkforceFormData) => {
    if (isNew) {
      await createDriver({
        tenantId,
        body: { ...values } as IWorkforce,
      })
        .then((res: any) => {
          if (!res?.error) {
            enqueue('Workforce created successfully')
          }
        })
        .catch(() => {
          enqueueFailureMsg('Failed to create workforce')
        })
    } else {
      await updateDriver({
        tenantId,
        body: { ...values } as IWorkforce,
      })
        .then((res: any) => {
          if (!res?.error) {
            enqueue('Workforce updated successfully')
          }
        })
        .catch(() => {
          enqueueFailureMsg('Failed to update workforce')
        })
    }
    onClose()
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: '56px', marginBottom: '82px' }}>
        <Box sx={{ m: '16px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <UserInfo isNew={isNew} isDisabled={isDisabled} />
          </Box>
          {userTypes?.find((ut) => ut.id === userType?.id)?.name === 'Driver' && (
            <Box sx={{ flex: 1, mr: 2 }}>
              <DriverInfo isNew={isNew} isDisabled={isDisabled} />
              <Box>
                <Documents
                  categoryId={1}
                  entityId={workforce.id}
                  tenantId={tenantId}
                  customerId={customerId}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            zIndex: 999,
          }}
        >
          {!isDisabled && <SubmitButtons create={isNew} isEnabled={true} onCancel={onClose} submitForm={handleSubmit(onSubmit)} />}
        </Box>
      </form>
    </FormProvider>
  )
}

export default WorkforceForm
